import { ComponentRef } from "@angular/core";
import { filter, merge, Observable, Subject, take } from "rxjs";
import { FocusOrigin } from "@angular/cdk/a11y";
import { DrawerPosition, VdsDrawerConfig } from "./drawer-config";
import { DialogRef } from "@angular/cdk/dialog";
import { ESCAPE, hasModifierKey } from "@angular/cdk/keycodes";
import { GlobalPositionStrategy } from "@angular/cdk/overlay";
import { VdsDrawerContainer } from "./drawer-container";

export const enum VdsDrawerState {
    OPEN = 0,
    CLOSING = 1,
    CLOSED = 2
}

export class VdsDrawerRef<T, R = any> {
    /** The instance of component opened into the drawer. */
    componentInstance!: T;

    /**
     * `ComponentRef` of the component opened into the drawer. Will be
     * null when the drawer is opened using a `TemplateRef`.
     */
    readonly componentRef: ComponentRef<T> | null = null;

    /** Whether the user is allowed to close the drawer. */
    disableClose: boolean | undefined;

    /** Unique ID for the drawer. */
    id: string;

    /** Subject for notifying the user that the drawer has finished opening. */
    private readonly _afterOpened = new Subject<void>();

    /** Subject for notifying the user that the drawer has started closing. */
    private readonly _beforeClosed = new Subject<R | undefined>();

    /** Result to be passed to afterClosed. */
    private _result: R | undefined;

    /** Handle to the timeout that's running as a fallback in case the exit animation doesn't fire. */
    private _closeFallbackTimeout: any | number;

    /** Current state of the dialog. */
    private _state = VdsDrawerState.OPEN;

    private _closeInteractionType: FocusOrigin | undefined;

    constructor(
        private _ref: DialogRef<R, T>,
        config: VdsDrawerConfig,
        public _containerInstance: VdsDrawerContainer
    ) {
        this.disableClose = config.disableClose;
        this.id = _ref.id;

        _containerInstance._animationStateChanged
            .pipe(
                filter((event) => event.state === "opened"),
                take(1)
            )
            .subscribe(() => {
                this._afterOpened.next();
                this._afterOpened.complete();
            });

        // Dispose overlay when closing animation is complete
        _containerInstance._animationStateChanged
            .pipe(
                filter((event) => event.state === "closed"),
                take(1)
            )
            .subscribe(() => {
                clearTimeout(this._closeFallbackTimeout);
                this._finishDrawerClose();
            });

        _ref.overlayRef.detachments().subscribe(() => {
            this._beforeClosed.next(this._result);
            this._beforeClosed.complete();
            this._finishDrawerClose();
        });

        merge(
            this.backdropClick(),
            this.keydownEvents().pipe(filter((event) => event.keyCode === ESCAPE && !this.disableClose && !hasModifierKey(event)))
        ).subscribe((event) => {
            if (!this.disableClose) {
                event.preventDefault();
                _closeDrawerVia(this, event.type === "keydown" ? "keyboard" : "mouse");
            }
        });
    }

    close(dialogResult?: R): void {
        this._result = dialogResult;

        this._containerInstance._animationStateChanged
            .pipe(
                filter((event) => event.state === "closing"),
                take(1)
            )
            .subscribe((event) => {
                this._beforeClosed.next(dialogResult);
                this._beforeClosed.complete();
                this._ref.overlayRef.detachBackdrop();

                // The logic that disposes of the overlay depends on the exit animation completing, however
                // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
                // timeout which will clean everything up if the animation hasn't fired within the specified
                // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
                // vast majority of cases the timeout will have been cleared before it has the chance to fire.
                this._closeFallbackTimeout = setTimeout(() => this._finishDrawerClose(), event.totalTime + 100);
            });

        this._state = VdsDrawerState.CLOSING;
        this._containerInstance._startExitAnimation();
    }

    /**
     * Gets an observable that is notified when the drawer is finished opening.
     */
    afterOpened(): Observable<void> {
        return this._afterOpened;
    }

    /**
     * Gets an observable that is notified when the drawer is finished closing.
     */
    afterClosed(): Observable<R | undefined> {
        return this._ref.closed;
    }

    /**
     * Gets an observable that is notified when the drawer has started closing.
     */
    beforeClosed(): Observable<R | undefined> {
        return this._beforeClosed;
    }

    /**
     * Gets an observable that emits when the overlay's backdrop has been clicked.
     */
    backdropClick(): Observable<MouseEvent> {
        return this._ref.backdropClick;
    }

    /**
     * Gets an observable that emits when keydown events are targeted on the overlay.
     */
    keydownEvents(): Observable<KeyboardEvent> {
        return this._ref.keydownEvents;
    }

    updatePosition(position?: DrawerPosition): this {
        const strategy = this._ref.config.positionStrategy as GlobalPositionStrategy;

        if (position && (position === "left" || position === "right")) {
            position === "left" ? strategy.left("0") : strategy.right("0");
        }

        this._ref.updatePosition();

        return this;
    }

    /**
     * Updates the drawers width.
     * @param width New width of the drawer.
     */
    updateSize(width = "", height: ""): this {
        this._ref.updateSize(width, height);
        return this;
    }

    /** Add a CSS class or an array of classes to the overlay pane. */
    addPanelClass(classes: string | string[]): this {
        this._ref.addPanelClass(classes);
        return this;
    }

    /** Remove a CSS class or an array of classes from the overlay pane. */
    removePanelClass(classes: string | string[]): this {
        this._ref.removePanelClass(classes);
        return this;
    }

    /** Gets the current state of the drawer's lifecycle. */
    getState(): VdsDrawerState {
        return this._state;
    }

    /**
     * Finishes the dialog close by updating the state of the dialog
     * and disposing the overlay.
     */
    private _finishDrawerClose() {
        this._state = VdsDrawerState.CLOSED;
        this._ref.close(this._result, { focusOrigin: this._closeInteractionType });
        this.componentInstance = null!;
    }
}

export function _closeDrawerVia<R>(ref: VdsDrawerRef<R>, interactionType: FocusOrigin, result?: R) {
    (ref as unknown as { _closeInteractionType: FocusOrigin })._closeInteractionType = interactionType;
    return ref.close(result);
}
